import React from "react";
import { ArrowRight, Phone, ShieldCheck, Camera, ScanLine, Eye, Building2, Mail } from "lucide-react";

const head = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const body = { fontFamily: "'IBM Plex Sans', sans-serif" };

const services = [
  { icon: ShieldCheck, title: "Dedicated Virtual Guards", desc: "One guard assigned exclusively to your community, monitoring remotely from our command center." },
  { icon: Camera, title: "Camera Installation", desc: "Full security infrastructure designed, installed and configured for live remote monitoring." },
  { icon: ScanLine, title: "License Plate Recognition", desc: "Automated capture and verification of every vehicle entering and leaving your property." },
  { icon: Eye, title: "Proactive Monitoring", desc: "Threats identified by behaviour and pattern — before they become incidents." },
  { icon: Building2, title: "Gate & Access Control", desc: "Remote visitor verification, resident recognition and delivery management." },
  { icon: Mail, title: "Mailbox Theft Prevention", desc: "Cluster mailbox retrofitting with privacy-compliant real-time monitoring." },
];

const Option1Authority = () => {
  return (
    <div style={body} className="bg-white text-[#1A1A1A]">
      {/* Nav */}
      <header className="border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-8 bg-[#D32F2F]" />
            <span style={head} className="text-xl font-extrabold tracking-tight">REDLINE</span>
            <span style={head} className="text-xl font-medium text-[#1A1A1A]/50">MONITORING</span>
          </div>
          <nav className="hidden lg:flex items-center gap-10 text-sm font-medium tracking-wide uppercase text-[#1A1A1A]/70">
            <a href="#" className="hover:text-[#D32F2F]">Guards</a>
            <a href="#" className="hover:text-[#D32F2F]">Why Dedicated</a>
            <a href="#" className="hover:text-[#D32F2F]">Communities</a>
            <a href="#" className="hover:text-[#D32F2F]">Standards</a>
          </nav>
          <div className="flex items-center gap-5">
            <span className="hidden md:flex items-center gap-2 text-sm font-semibold"><Phone className="w-4 h-4 text-[#D32F2F]" />(512) 363-6584</span>
            <button className="bg-[#D32F2F] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wide rounded-none hover:bg-[#1A1A1A] transition-colors">Request Consultation</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-16 items-center py-24">
        <div>
          <div className="inline-flex items-center gap-2 border border-[#1A1A1A]/15 px-4 py-2 mb-10 text-xs font-semibold uppercase tracking-[0.2em]">
            <span className="w-2 h-2 bg-[#D32F2F]" /> Certified HOA Protection
          </div>
          <h1 style={head} className="text-6xl xl:text-7xl font-extrabold leading-[0.95] tracking-tight mb-8">
            Elite virtual<br />security for<br /><span className="text-[#D32F2F]">your community.</span>
          </h1>
          <p className="text-lg text-[#1A1A1A]/70 max-w-xl mb-10 leading-relaxed">
            A dedicated guard who knows your residents, your gates and your routines — monitoring remotely, 24/7, from our command center. Institutional-grade protection trusted by HOA boards.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-[#1A1A1A] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wide flex items-center gap-3 hover:bg-[#D32F2F] transition-colors">Schedule Consultation <ArrowRight className="w-4 h-4" /></button>
            <button className="border border-[#1A1A1A]/20 px-8 py-4 text-sm font-semibold uppercase tracking-wide hover:border-[#D32F2F] hover:text-[#D32F2F] transition-colors">See How It Works</button>
          </div>
          <div className="grid grid-cols-3 gap-8 mt-16 pt-10 border-t border-[#1A1A1A]/10">
            {[["200+", "Communities"], ["75%", "Incident Drop"], ["98%", "Retention"]].map(([n, l]) => (
              <div key={l}>
                <div style={head} className="text-4xl font-extrabold">{n}</div>
                <div className="text-xs uppercase tracking-widest text-[#1A1A1A]/50 mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1775112077888-8fa36e9bbc51?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Gated community" className="w-full h-[560px] object-cover grayscale" />
          <div className="absolute bottom-0 left-0 right-0 bg-white border border-[#1A1A1A]/10 p-6 m-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D32F2F] font-semibold mb-2"><span className="w-2 h-2 bg-[#D32F2F] animate-pulse" /> Live Command Center</div>
            <p style={head} className="text-lg font-bold">Your guard is watching, right now.</p>
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section className="bg-[#F5F5F5] border-y border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="flex items-end justify-between mb-14">
            <h2 style={head} className="text-4xl xl:text-5xl font-extrabold tracking-tight max-w-md">A complete security institution.</h2>
            <p className="text-[#1A1A1A]/60 max-w-sm hidden lg:block">Not just monitoring — we install, verify, report and protect end to end.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
            {services.map((s) => (
              <div key={s.title} className="bg-white p-10 group hover:bg-[#1A1A1A] transition-colors duration-300">
                <s.icon className="w-8 h-8 text-[#D32F2F] mb-6" />
                <h3 style={head} className="text-xl font-bold mb-3 group-hover:text-white transition-colors">{s.title}</h3>
                <p className="text-sm text-[#1A1A1A]/60 leading-relaxed group-hover:text-white/70 transition-colors">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-7xl mx-auto px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <blockquote style={head} className="text-3xl xl:text-4xl font-medium leading-snug">
          “Insurance claims dropped <span className="text-[#D32F2F]">80%</span> since switching to Redline's dedicated guard model. The consistency is incredible.”
        </blockquote>
        <div className="border-l-2 border-[#D32F2F] pl-6">
          <p className="font-semibold">Robert Johnson</p>
          <p className="text-sm text-[#1A1A1A]/60">HOA Treasurer · Brushy Creek Gates, 150 homes</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-8 py-20 flex flex-col lg:flex-row items-center justify-between gap-8">
          <h2 style={head} className="text-4xl xl:text-5xl font-extrabold tracking-tight">Protect your community today.</h2>
          <button className="bg-[#D32F2F] px-10 py-5 text-sm font-semibold uppercase tracking-wide flex items-center gap-3 hover:bg-white hover:text-[#1A1A1A] transition-colors">Request a Consultation <ArrowRight className="w-4 h-4" /></button>
        </div>
      </section>
    </div>
  );
};

export default Option1Authority;
